import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const BookingDetailModal = ({ isOpen, onClose, booking, onUpdate, services, staff, clients, rooms }) => {
  const [client, setClient] = useState(null);
  const [service, setService] = useState(null);
  const [staffMember, setStaffMember] = useState(null);
  const [room, setRoom] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(booking.payment_status);
  const [bookingStatus, setBookingStatus] = useState(booking.status);
  const [additionalPayment, setAdditionalPayment] = useState(0);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const clientData = clients.find((c) => c.id === booking.client_id);
    const serviceData = services.find((s) => s.id === booking.service_id);
    const staffData = staff.find((s) => s.id === booking.staff_id);
    const roomData = rooms.find((r) => r.id === booking.room_id);

    setClient(clientData);
    setService(serviceData);
    setStaffMember(staffData);
    setRoom(roomData);

    // Load booking history
    loadClientHistory(booking.client_id);
  }, [booking, clients, services, staff, rooms]);

  const loadClientHistory = async (clientId) => {
    try {
      const response = await api.getBookings();
      const clientBookings = response.data.filter((b) => b.client_id === clientId && b.id !== booking.id);
      setHistory(clientBookings.slice(0, 5));
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const handleUpdatePayment = async () => {
    try {
      await api.updateBookingPayment(booking.id, paymentStatus, additionalPayment);
      toast.success('Pago actualizado');
      onUpdate();
    } catch (error) {
      toast.error('Error al actualizar pago');
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await api.updateBookingStatus(booking.id, bookingStatus);
      toast.success('Estado actualizado');
      onUpdate();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      completed: 'Completada',
      cancelled: 'Cancelada',
    };
    return <Badge className={variants[status]}>{labels[status]}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const variants = {
      paid: 'bg-[#6B7C59] text-white',
      partial: 'bg-[#C9A961] text-white',
      unpaid: 'bg-red-500 text-white',
    };
    const labels = {
      paid: 'Pagado',
      partial: 'Seña',
      unpaid: 'Sin pago',
    };
    return <Badge className={variants[status]}>{labels[status]}</Badge>;
  };

  if (!client || !service || !staffMember || !room) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl h-[600px] flex flex-col" data-testid="booking-detail-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-cormorant font-bold text-[#C9A961]">
            Detalle de Reserva
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="details" className="flex-1 flex flex-col mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details">Detalles y Pago</TabsTrigger>
            <TabsTrigger value="history">Historial Cliente</TabsTrigger>
          </TabsList>

          {/* Details & Payment Tab - UNIFIED */}
          <TabsContent value="details" className="flex-1 overflow-y-auto mt-4">
            <div className="space-y-4 max-h-[450px] overflow-y-auto pr-2">
              {/* Client & Service Info - 2 columns */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-xs font-semibold text-[#6B6B6B] mb-1">Cliente</h3>
                  <p className="text-base font-medium text-[#2C2C2C]">{client.name}</p>
                  <p className="text-xs text-[#6B6B6B]">{client.phone}</p>
                  {client.email && <p className="text-xs text-[#6B6B6B]">{client.email}</p>}
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-[#6B6B6B] mb-1">Servicio</h3>
                  <p className="text-base font-medium text-[#2C2C2C]">{service.name}</p>
                  <p className="text-xs text-[#6B6B6B]">Duración: {service.duration_minutes} min</p>
                </div>
              </div>

              {/* Date/Time & Staff/Room - 2 columns */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-xs font-semibold text-[#6B6B6B] mb-1">Fecha y Hora</h3>
                  <p className="text-base font-medium text-[#2C2C2C]">
                    {format(parseISO(booking.booking_datetime), 'dd MMM yyyy', { locale: es })}
                  </p>
                  <p className="text-xs text-[#6B6B6B]">
                    {format(parseISO(booking.booking_datetime), 'HH:mm', { locale: es })} hs
                  </p>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-[#6B6B6B] mb-1">Personal y Sala</h3>
                  <p className="text-sm text-[#2C2C2C]">Personal: {staffMember.name}</p>
                  <p className="text-sm text-[#2C2C2C]">Sala: {room.name}</p>
                </div>
              </div>

              {/* Status & Payment Info - Compact */}
              <div className="border-t border-[#C9A961]/20 pt-3">
                <h3 className="text-xs font-semibold text-[#6B6B6B] mb-2">Estado y Pago</h3>
                
                {/* Payment Summary - 3 columns */}
                <div className="grid grid-cols-3 gap-3 bg-[#F5F2ED] p-3 rounded-lg text-xs mb-3">
                  <div>
                    <p className="text-[#6B6B6B] mb-0.5">Total:</p>
                    <p className="font-semibold text-[#2C2C2C]">
                      ${booking.total_amount?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#6B6B6B] mb-0.5">Pagado:</p>
                    <p className="font-semibold text-[#2C2C2C]">
                      ${booking.deposit_amount?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#6B6B6B] mb-0.5">Saldo:</p>
                    <p className="font-bold text-[#C9A961]">
                      ${((booking.total_amount || 0) - (booking.deposit_amount || 0)).toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Payment & Status Actions - 2 columns */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Payment Actions */}
                  <div>
                    <Label className="text-xs font-semibold text-[#6B6B6B] mb-2 block">Registrar Pago</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        value={additionalPayment}
                        onChange={(e) => setAdditionalPayment(parseFloat(e.target.value) || 0)}
                        placeholder="Monto"
                        className="text-sm h-8"
                      />
                      <Button onClick={handleUpdatePayment} className="bg-[#6B7C59] hover:bg-[#5a6a4a] h-8 text-xs px-3">
                        Pagar
                      </Button>
                    </div>
                  </div>

                  {/* Status Update */}
                  <div>
                    <Label className="text-xs font-semibold text-[#6B6B6B] mb-2 block">Cambiar Estado</Label>
                    <div className="flex gap-2">
                      <Select value={bookingStatus} onValueChange={setBookingStatus}>
                        <SelectTrigger className="text-sm h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="reserved">Reservado</SelectItem>
                          <SelectItem value="confirmed_deposit">Conf. Seña</SelectItem>
                          <SelectItem value="confirmed_full">Conf. Completo</SelectItem>
                          <SelectItem value="confirmed_manual">Conf. Manual</SelectItem>
                          <SelectItem value="completed_paid">Finalizado</SelectItem>
                          <SelectItem value="cancelled">Cancelado</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={handleUpdateStatus} className="bg-[#C9A961] hover:bg-[#B89551] h-8 text-xs px-3">
                        OK
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {booking.notes && (
                <div className="border-t border-[#C9A961]/20 pt-3">
                  <h3 className="text-xs font-semibold text-[#6B6B6B] mb-1">Notas</h3>
                  <p className="text-xs text-[#2C2C2C] bg-[#F5F2ED] p-2 rounded">{booking.notes}</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="flex-1 overflow-y-auto mt-4">
            <div className="space-y-3 max-h-[450px] overflow-y-auto pr-2">
              <h3 className="text-lg font-cormorant font-semibold text-[#2C2C2C] sticky top-0 bg-white pb-2 z-10">
                Historial de Reservas del Cliente
              </h3>

              {history.length === 0 ? (
                <p className="text-[#6B6B6B] text-center py-8">No hay historial previo de este cliente</p>
              ) : (
                <div className="space-y-3">
                  {history.map((hist) => {
                    const histService = services.find((s) => s.id === hist.service_id);
                    return (
                      <div
                        key={hist.id}
                        className="p-4 border border-[#C9A961]/20 rounded-lg hover:bg-[#F5F2ED] transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-[#2C2C2C]">{histService?.name || 'Servicio'}</p>
                            <p className="text-sm text-[#6B6B6B] mt-1">
                              {format(parseISO(hist.booking_datetime), 'dd MMM yyyy - HH:mm', { locale: es })}
                            </p>
                            <p className="text-xs text-[#6B6B6B] mt-1">
                              Total: ${hist.total_amount?.toFixed(2)} {hist.currency || 'UYU'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end space-y-1">
                            {getStatusBadge(hist.status)}
                            {getPaymentBadge(hist.payment_status)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-[#C9A961]/20">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDetailModal;